// src/pages/reports/NewReport.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Timestamp } from "firebase/firestore";

// --- PREMIUM UI TOKENS (Standardized for Reports) ---
const UI_THEME = {
  bg: "#f8fafc",
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  secondary: "#f1f5f9",
  accent: "#10b981",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const filterCardStyle = {
  background: UI_THEME.surface,
  padding: "24px",
  borderRadius: "16px",
  boxShadow: UI_THEME.shadow,
  border: `1px solid ${UI_THEME.border}`,
  display: "flex",
  gap: "20px",
  alignItems: "flex-end",
  marginBottom: "32px",
  flexWrap: "wrap",
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: UI_THEME.muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: `1px solid ${UI_THEME.border}`,
  fontSize: "14px",
  fontWeight: 600,
  color: UI_THEME.text,
  outline: "none",
  minWidth: "160px",
};

const primaryBtn = {
  padding: "10px 24px",
  borderRadius: "10px",
  background: UI_THEME.primary,
  color: "#fff",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)",
};

const secondaryBtn = {
  padding: "10px 20px",
  borderRadius: "10px",
  background: "#f1f5f9",
  color: "#475569",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `2px solid ${UI_THEME.border}`,
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  padding: "14px 20px",
  fontSize: "14px",
  color: UI_THEME.text,
  borderBottom: `1px solid #f1f5f9`,
};

function formatNumber(v) {
  return Number(v || 0).toFixed(2);
}

function formatQty(v) {
  return Number(v || 0).toFixed(3);
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDDMMYYYY(ymd) {
  if (!ymd) return "";
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function toStartOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function toEndOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(d);
}

export default function NewReport() {
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [totals, setTotals] = useState({
    Qty: 0,
    BasicAmount: 0,
    TaxAmount: 0,
    AddlCessAmount: 0,
	ewayAmount: 0,
    
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);

      const from = opts.fromDate || fromDate;
      const to = opts.toDate || toDate;

      const qRef = collection(db, "orders");
      const q = query(
  collection(db, "orders"),
  where("status", "==", "APPROVED"),
  where("approvedAt", ">=", toStartOfDayTs(from)),
  where("approvedAt", "<=", toEndOfDayTs(to)),
  orderBy("approvedAt", "asc")
);

      const snap = await getDocs(q);
      const map = new Map();

      snap.docs.forEach((docSnap) => {
        const o = docSnap.data();
        const date = o.orderDateOnly || today();
        const lines = Array.isArray(o.orderLines) ? o.orderLines : [];

        lines.forEach((L) => {
          const itemCode = L.itemCode || "";
          const key = `${date}__${itemCode}`;

          if (!map.has(key)) {
            map.set(key, {
              date,
              itemCode,
              brand: L.brand || "",
              Rate: Number(L.rate || 0),
              Qty: 0,
              BasicAmount: 0,
              TaxAmount: 0,
              AddlCessAmount: 0,
			  ewayAmount: 0
              
            });
          }

          const g = map.get(key);

          const qty = Number(L.qty || 0);
          const basicAmount = Number(L.basicAmount || 0);
          const gstAmount = Number(L.gstAmount || 0);
          const cessAmount = Number(L.cessAmount || 0);
          const taxAmount = gstAmount + cessAmount;
          const addlCessAmount = Number(L.addlCessAmount || 0);
          const ewayAmount = Number(L.ewayAmount || 0); // no rebate in this report

		  //const totalAmount = Number(L.totalAmount || 0); // no rebate in this report

          g.Qty += qty;
          g.BasicAmount += basicAmount;
          g.TaxAmount += taxAmount;
          g.AddlCessAmount += addlCessAmount;
          g.ewayAmount += ewayAmount; // no rebate
        });
      });

      const rows = Array.from(map.values()).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return a.itemCode.localeCompare(b.itemCode);
      });

      setAllRows(rows);
      computeTotals(rows);
      setPage(1);

    } catch (err) {
      console.error(err);
      alert("Error loading new report");
    } finally {
      setLoading(false);
    }
  };

  const computeTotals = (rows) => {
    const t = {
      Qty: 0,
      BasicAmount: 0,
      TaxAmount: 0,
      AddlCessAmount: 0,
	  ewayAmount: 0
      
    };

    rows.forEach((r) => {
      t.Qty += Number(r.Qty || 0);
      t.BasicAmount += Number(r.BasicAmount || 0);
      t.TaxAmount += Number(r.TaxAmount || 0);
      t.AddlCessAmount += Number(r.AddlCessAmount || 0);
      t.ewayAmount += Number(r.ewayAmount || 0);
    });

    setTotals(t);
  };

  const visibleRows = useMemo(() => {
    const txt = (searchText || "").toLowerCase();
    const filtered = allRows.filter((r) => {
      if (!txt) return true;
      return (
        (r.itemCode || "").toLowerCase().includes(txt) ||
        (r.brand || "").toLowerCase().includes(txt) ||
        (r.date || "").toLowerCase().includes(txt)
      );
    });

    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [searchText, allRows, page]);

  const exportExcel = () => {
    const rows = allRows.map((r) => ({
      Date: r.date,
      ItemCode: r.itemCode,
      Brand: r.brand,
      Rate: r.Rate,
      Qty: r.Qty,
      BasicAmount: r.BasicAmount,
      TaxAmount: r.TaxAmount,
      AddlCessAmount: r.AddlCessAmount,
      ewayAmount: r.ewayAmount,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NewReport");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `NewReport_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <Layout>
      <div style={{ padding: "32px", background: UI_THEME.bg, minHeight: "calc(100vh - 80px)" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 800, color: UI_THEME.text, margin: 0 }}>Daily Item Report</h1>
            <p style={{ color: UI_THEME.muted, fontWeight: 500, marginTop: "4px" }}>Item-level transaction analysis & volume breakdown</p>
          </div>
          <button onClick={exportExcel} style={secondaryBtn}>
            📥 Export to Excel
          </button>
        </div>

        {/* FILTER CARD */}
        <div style={filterCardStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date From</label>
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date To</label>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} style={inputStyle} />
          </div>
          <button onClick={() => loadData({fromDate, toDate})} style={primaryBtn}>Generate</button>
          
          <div style={{ ...inputGroupStyle, marginLeft: "auto", flex: 1, maxWidth: "400px" }}>
            <label style={labelStyle}>Search results</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", opacity: 0.5 }}>🔍</span>
              <input 
                placeholder="Search item, brand or date..." 
                value={searchText} 
                onChange={(e)=>setSearchText(e.target.value)} 
                style={{ ...inputStyle, width: "100%", paddingLeft: "36px" }} 
              />
            </div>
          </div>
        </div>

        {/* DATA TABLE CARD */}
        <div style={{ 
          background: UI_THEME.surface, 
          borderRadius: "16px", 
          boxShadow: UI_THEME.shadow, 
          border: `1px solid ${UI_THEME.border}`,
          overflow: "hidden"
        }}>
          <div style={{ overflowX: "auto", maxHeight: "600px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Item Code</th>
                  <th style={tableHeaderStyle}>Brand</th>
                  <th style={tableHeaderStyle}>Rate (Basic+Eway)</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Qty</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Basic Amount</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Tax Amount</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Addl Cess</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Eway Amount</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>⏳</div>
                      Fetching item report...
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>📭</div>
                      No items matched your criteria.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((r,i)=>(
                    <tr key={i} style={{ 
                      background: i % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.2s"
                    }}>
                      <td style={tableCellStyle}>{formatDDMMYYYY(r.date)}</td>
                      <td style={{ ...tableCellStyle, fontWeight: 700 }}>{r.itemCode}</td>
                      <td style={{ ...tableCellStyle, color: "#4f46e5", fontWeight: 800 }}>{r.brand}</td>
                      <td style={tableCellStyle}>{formatNumber(r.Rate)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{formatQty(r.Qty)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.BasicAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.TaxAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.AddlCessAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.ewayAmount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && visibleRows.length > 0 && (
                <tfoot style={{ background: "#f8fafc", borderTop: `2px solid ${UI_THEME.border}`, position: "sticky", bottom: 0 }}>
                  <tr style={{ fontWeight: 800, color: UI_THEME.text }}>
                    <td colSpan="4" style={{ ...tableCellStyle, textAlign: "right" }}>GRAND TOTAL</td>
                    <td style={{ ...tableCellStyle, textAlign: "center" }}>{formatQty(totals.Qty)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.BasicAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.TaxAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.AddlCessAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.ewayAmount)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* PAGINATION BAR */}
          <div style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${UI_THEME.border}` }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: UI_THEME.muted }}>
              Showing {visibleRows.length} of {allRows.length} items
            </span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button 
                onClick={()=>setPage(Math.max(1, page-1))} 
                disabled={page === 1} 
                style={{ ...secondaryBtn, padding: "8px 16px", opacity: page === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span style={{ fontWeight: 700, fontSize: "14px", color: UI_THEME.text }}>Page {page}</span>
              <button 
                onClick={()=>setPage(page+1)} 
                style={{ ...secondaryBtn, padding: "8px 16px", opacity: visibleRows.length < PAGE_SIZE ? 0.5 : 1 }}
                disabled={visibleRows.length < PAGE_SIZE}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
